import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Info, ChevronDown, CheckCircle, Loader2, Calendar, Check, RefreshCw, UserPlus, X, Search } from 'lucide-react';
import { SiQuickbooks } from 'react-icons/si';
import { useDemoMode } from '../../../../context/DemoModeContext';
import {
  simulateCreateStripeIntegration as simulateSync,
  DEMO_ENVIRONMENT,
} from '../Stripe/components/stripeDemoHelpers';
import { useCreateAirbyteIntegrationMutation } from '../../../../store/graphql/codegen/auto-gen/revenue-report';
import { useGetQuickbooksItemsQuery, useTestBillingConnectionLazyQuery, useGetQuickbooksCredentialsLazyQuery } from '../../../../store/graphql/codegen/auto-gen/app-pricing';
import { revenueClient, appPricingClient } from '../../../../store/graphql/apollo/clients';
import { EmailManager } from '../RevenueAnalytics';
import { QuickBooksSetupGuide } from './components/QuickBooksSetupGuide';
import { FixInstructionsContent } from '../Stripe/components/FixInstructionsModal';
import { InviteTeamModal } from '../../../Onboarding/Common/InviteTeamModal';
import { TeamId } from 'store/redux/reducers/team/teamConstants';
import {
  getQuickBooksFormData,
  setQuickBooksFormData,
  removeQuickBooksFormData,
  getUser,
  getEnvironmentId,
  getEnvironmentDisplayName,
} from 'utils/localStorage';

interface QuickBooksIntegrationProps {
  sourceConfigurations: Record<string, any>;
  updateSourceConfiguration: (sourceId: string, config: any) => void;
  onStepComplete: (value: boolean, overrideValues?: any) => void;
  emailManager: EmailManager;
  onAutoSave: () => void;
  cleanOAuthParams: () => void;
  openSetupGuideRef?: React.MutableRefObject<(() => void) | null>;
  onTrackCompletion?: () => void;
}

interface FormData {
  sourceName: string;
  startDate: string;
  realmId: string;
  companyName: string;
}

type StepId = 'environment' | 'basic' | 'test' | 'pause-fix' | 'recurring' | 'plans' | 'professional' | 'cancellations' | 'ingestion';

type PlanTerm = 'day' | 'week' | 'month' | 'year';
interface PlanConfig { term: PlanTerm; frequency: number; }

const TERM_FREQUENCIES: Record<PlanTerm, number[]> = {
  day:   [1, 7, 14, 21],
  week:  [1, 2, 4],
  month: [1, 3, 6],
  year:  [1],
};

const FREQUENCY_LABEL = (term: PlanTerm, freq: number) => {
  const unit = term === 'day' ? 'day' : term === 'week' ? 'week' : term === 'month' ? 'month' : 'year';
  return freq === 1 ? `Every ${unit}` : `Every ${freq} ${unit}s`;
};

type TestStatus = 'pending' | 'loading' | 'success' | 'error';

interface TestingStep {
  id: string;
  title: string;
  loadingText: string;
  successText: string;
  errorText: string;
  status: TestStatus;
  errorMessage?: string;
  details?: string;
  accessibleEntities?: string[];
  missingEntities?: string[];
}

const QB_OAUTH_URL = 'https://appcenter.intuit.com/connect/oauth2';
const QB_SCOPE = 'com.intuit.quickbooks.accounting';
const QB_CLIENT_ID_SANDBOX = process.env.QUICKBOOKS_CLIENT_ID as string;
const QB_CLIENT_ID_PRODUCTION = process.env.QUICKBOOKS_PROD_CLIENT_ID as string;
const QB_OAUTH_POSTMESSAGE_TYPE = 'QB_OAUTH_CALLBACK';

const getDefaultDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 12);
  return d.toISOString().slice(0, 10);
};

type DatePeriod = '' | '1m' | '3m' | '6m' | '12m' | 'custom';

const DATE_PERIOD_LABELS: Record<string, string> = {
  '1m': '1 Month', '3m': '3 Months', '6m': '6 Months', '12m': '12 Months',
};

const computeStartDateFromPeriod = (period: '1m' | '3m' | '6m' | '12m'): string => {
  const d = new Date();
  const months = period === '1m' ? 1 : period === '3m' ? 3 : period === '6m' ? 6 : 12;
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
};

const INITIAL_FORM: FormData = {
  sourceName: 'QuickBooks',
  startDate: getDefaultDate(),
  realmId: '',
  companyName: '',
};

const LabelRow = ({ text }: { text: string }) => (
  <div className="flex items-center gap-1 mb-1.5">
    <span className="text-sm font-medium text-gray-700">{text}</span>
    <Info className="w-3.5 h-3.5 text-gray-400" />
  </div>
);

const BASE_STEPS: { id: StepId; num: number; title: string; description: string }[] = [
  { id: 'environment', num: 1, title: 'Map Environments', description: 'Select the QuickBooks environment to connect with your App environment' },
  { id: 'basic',       num: 2, title: 'Connect QuickBooks', description: 'Authorize ThriveStack to access your QuickBooks data via OAuth' },
  { id: 'test',        num: 3, title: 'Test Connection', description: 'Verify your QuickBooks connection before starting data ingestion' },
  { id: 'ingestion',   num: 4, title: 'Start Ingestion', description: 'Trigger the data sync from your QuickBooks account to ThriveStack' },
];

const DEMO_ITEMS = [
  { name: 'Implementation Services', type: 'Service',      price: '$150' },
  { name: 'Technical Support',       type: 'Service',      price: '$100' },
  { name: 'Onboarding & Training',   type: 'Service',      price: '$120' },
  { name: 'Professional Plan License', type: 'NonInventory', price: '$49' },
  { name: 'Enterprise Plan License', type: 'NonInventory', price: '$199' },
  { name: 'API Access Add-on',       type: 'NonInventory', price: '$29'  },
];

export const QuickBooksIntegration: React.FC<QuickBooksIntegrationProps> = ({
  sourceConfigurations,
  updateSourceConfiguration,
  onStepComplete,
  emailManager,
  onAutoSave,
  cleanOAuthParams,
  openSetupGuideRef,
  onTrackCompletion,
}) => {
  const { isDemoMode } = useDemoMode();
  const saved = sourceConfigurations?.quickbooks || {};
  const oauthHandled = useRef(false);

  const [isSetupGuideOpen, setIsSetupGuideOpen] = useState(false);

  useEffect(() => {
    if (openSetupGuideRef) {
      openSetupGuideRef.current = () => setIsSetupGuideOpen(true);
      return () => { if (openSetupGuideRef) openSetupGuideRef.current = null; };
    }
  }, [openSetupGuideRef]);

  const primaryEmail = getUser()?.email ?? '';
  const buildNotificationEmails = () => {
    const additional = emailManager.state.emails.filter(e => e !== primaryEmail);
    return primaryEmail ? [primaryEmail, ...additional] : additional;
  };

  const savedAdditionalEmails = (saved.notificationEmails ?? []).filter((e: string) => e !== primaryEmail);
  const prevEmailsRef = useRef<string[]>(savedAdditionalEmails);
  const emailsReady = useRef(false);

  useEffect(() => {
    if (savedAdditionalEmails.length) {
      emailManager.setEmails(savedAdditionalEmails);
    }
    emailsReady.current = true;
  }, []);

  useEffect(() => {
    if (!emailsReady.current) return;
    const curr = emailManager.state.emails;
    const prev = prevEmailsRef.current;
    if (curr.length === prev.length && curr.every((e, i) => e === prev[i])) return;
    prevEmailsRef.current = curr;
    save({ notificationEmails: buildNotificationEmails() });
  }, [emailManager.state.emails]);

  // Real environment data (non-demo) or demo mock
  const envId = isDemoMode ? DEMO_ENVIRONMENT.id : (getEnvironmentId() ?? '');
  const envDisplayName = isDemoMode ? DEMO_ENVIRONMENT.displayName : (getEnvironmentDisplayName() ?? 'Production');

  // Determine if ThriveStack environment is production for QB auto-mapping
  const thriveEnvIsProduction = isDemoMode
    ? (DEMO_ENVIRONMENT.environmentType === 'PRODUCTION' || DEMO_ENVIRONMENT.name?.toLowerCase() === 'production')
    : envDisplayName.toLowerCase().includes('prod');

  const recommendedQBEnvironment: 'Production' | 'Sandbox' = thriveEnvIsProduction ? 'Production' : 'Sandbox';

  const [step1Completed, setStep1Completed] = useState(!!saved.step1Completed);
  const [step2Completed, setStep2Completed] = useState(!!saved.step2Completed);
  const [step3Completed, setStep3Completed] = useState(!!saved.step3Completed);
  const [pauseFixCompleted, setPauseFixCompleted] = useState(!!saved.pauseFixCompleted);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showFixDrawer, setShowFixDrawer] = useState(false);
  const [itemsStepCompleted, setItemsStepCompleted] = useState(!!saved.itemsStepCompleted);
  const [plansStepCompleted, setPlansStepCompleted] = useState(!!saved.plansStepCompleted);
  const [professionalStepCompleted, setProfessionalStepCompleted] = useState(!!saved.professionalStepCompleted);
  const [cancellationsStepCompleted, setCancellationsStepCompleted] = useState(!!saved.cancellationsStepCompleted);
  const [cancellationPolicy, setCancellationPolicy] = useState<'auto' | 'manual'>(saved.cancellationPolicy || 'auto');
  const [unpaidInvoiceDays, setUnpaidInvoiceDays] = useState<number>(saved.unpaidInvoiceDays ?? 45);
  const [noRenewalDays, setNoRenewalDays] = useState<number>(saved.noRenewalDays ?? 45);
  const [planTerms, setPlanTerms] = useState<Record<string, PlanConfig>>(
    saved.mrrItemsTerm ? { ...saved.mrrItemsTerm } : {}
  );
  const [step4Completed, setStep4Completed] = useState(!!saved.step4Completed);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(
    new Set(saved.mrrItems || [])
  );
  const [itemSearch, setItemSearch] = useState('');
  const [otherRevenueItems, setOtherRevenueItems] = useState<Set<string>>(
    new Set(saved.oneTimeItems || [])
  );
  const [otherItemSearch, setOtherItemSearch] = useState('');
  const [datePeriod, setDatePeriod] = useState<DatePeriod>(saved.datePeriod || '');

  const { data: qbItemsData, loading: qbItemsLoading, error: qbItemsError } = useGetQuickbooksItemsQuery({
    client: appPricingClient,
    variables: {
      input: {
        accessToken: saved.accessToken || '',
        realmId: saved.realmId || '',
        sandbox: saved.sandbox || false,
      },
    },
    skip: isDemoMode || !saved.accessToken || !saved.realmId,
  });

  const STEPS = [
    ...BASE_STEPS.slice(0, 3),
    { id: 'pause-fix'     as StepId, num: 4, title: 'Fix Schema Issue',                                                  description: 'Add the group_id custom field to your QuickBooks customers to enable account-level correlation' },
    { id: 'recurring'     as StepId, num: 5, title: 'Select products that contribute to Recurring Revenue',              description: 'Products/services tied to subscriptions. These will power metrics like MRR, ARR, ACV, Contraction, Expansion etc.' },
    { id: 'plans'         as StepId, num: 6, title: 'Define Billing Terms',                                              description: 'Set the billing term and frequency for each product that contributes to your recurring revenue.' },
    { id: 'professional'     as StepId, num: 7, title: 'Select Services that Contribute to Non-Recurring Revenue',  description: 'Products/Services that are one-time, project-based, professional services, onboarding or ad-hoc services that generate revenue outside subscriptions' },
    { id: 'cancellations'    as StepId, num: 8, title: 'Subscription Cancellations',                                               description: 'Define how QuickBooks subscriptions should be cancelled when invoices go unpaid or renewals stop' },
    { id: 'ingestion'        as StepId, num: 9, title: 'Start Ingestion',                                             description: 'Trigger the data sync from your QuickBooks account to ThriveStack' },
  ];

  const [activeStep, setActiveStep] = useState<StepId>(
    saved.step4Completed                  ? 'ingestion'
      : saved.cancellationsStepCompleted  ? 'ingestion'
      : saved.professionalStepCompleted   ? 'cancellations'
      : saved.plansStepCompleted          ? 'professional'
      : saved.itemsStepCompleted          ? 'plans'
      : saved.pauseFixCompleted           ? 'recurring'
      : saved.step3Completed              ? 'pause-fix'
      : saved.step2Completed              ? 'test'
      : saved.step1Completed              ? 'basic'
      : 'environment'
  );

  // Auto-map: non-production ThriveStack → Sandbox, production → Production
  const [quickbooksEnvironment, setQuickbooksEnvironment] = useState<'Production' | 'Sandbox'>(
    saved.quickbooksEnvironment || recommendedQBEnvironment
  );
  const [envDropdownOpen, setEnvDropdownOpen] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    ...INITIAL_FORM,
    sourceName: saved.sourceName || INITIAL_FORM.sourceName,
    startDate: saved.startDate || INITIAL_FORM.startDate,
    realmId: saved.realmId || '',
    companyName: saved.companyName || '',
  });

  const [oauthConnecting, setOauthConnecting] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualCreds, setManualCreds] = useState({
    clientId: '',
    clientSecret: '',
    accessToken: '',
    refreshToken: '',
    realmId: '',
    companyName: '',
  });
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncElapsed, setSyncElapsed] = useState(0);
  const syncTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [syncResult, setSyncResult] = useState<{ syncStartedAt?: string } | null>(saved.ingestionResult || null);

  const [testingSteps, setTestingSteps] = useState<TestingStep[]>([
    { id: 'connection', title: 'Connection',         loadingText: 'Establishing Connection', successText: 'Connected',      errorText: 'Connection Failed',   status: 'pending' },
    { id: 'data',       title: 'Data Retrieval',     loadingText: 'Retrieving Data',         successText: 'Data Retrieved', errorText: 'Failed to Retrieve Data', status: 'pending' },
    { id: 'schema',     title: 'Schema Verification',loadingText: 'Verifying Schema',        successText: 'Schema Verified',errorText: 'Schema Error Detected',  status: 'pending' },
  ]);
  const [isTesting, setIsTesting] = useState(false);
  const [testCompleted, setTestCompleted] = useState(!!saved.testCompleted);
  const [groupIdFound, setGroupIdFound] = useState<boolean>(saved.groupIdFound ?? false);
  const [customerMetaDataKeys, setCustomerMetaDataKeys] = useState<string[]>(saved.customerMetaDataKeys || []);
  const [isTablesExpanded, setIsTablesExpanded] = useState(false);
  const mappingConfirmed = !!saved.fieldMapping?.confirmed;
  const [selectedProductField, setSelectedProductField] = useState<'accountId' | 'useremail' | null>(mappingConfirmed ? (saved.fieldMapping?.productField ?? null) : null);
  const [selectedQbField, setSelectedQbField] = useState<'customerId' | 'customerName' | null>(mappingConfirmed ? (saved.fieldMapping?.qbField ?? null) : null);
  const [fieldMappingConfirmed, setFieldMappingConfirmed] = useState<boolean>(mappingConfirmed);

  const [createAirbyteIntegration, { loading: createIntegrationLoading }] = useCreateAirbyteIntegrationMutation({
    client: revenueClient,
    fetchPolicy: 'no-cache',
  });

  const [runTestBillingConnection] = useTestBillingConnectionLazyQuery({
    client: appPricingClient,
    fetchPolicy: 'no-cache',
  });

  const [fetchQuickbooksCredentials] = useGetQuickbooksCredentialsLazyQuery({
    client: appPricingClient,
    fetchPolicy: 'no-cache',
  });

  const setField = <K extends keyof FormData>(key: K, value: FormData[K]) =>
    setFormData(prev => ({ ...prev, [key]: value }));

  const save = (patch: Record<string, any>) => {
    updateSourceConfiguration('quickbooks', patch);
    onAutoSave();
  };

  const inputBase = 'w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';

  // ── Session key (invalidate credentials on page refresh) ────────────────────
  const QB_SESSION_KEY = 'qb_oauth_session';

  // ── Shared OAuth callback processor ─────────────────────────────────────────

  const applyOAuthCallback = async (code: string, realmId: string, parsedState: Record<string, any>) => {
    const savedRaw = getQuickBooksFormData();
    let restoredForm: Partial<FormData> = {};
    if (savedRaw) {
      try { restoredForm = JSON.parse(savedRaw); } catch { }
      removeQuickBooksFormData();
    }
    const environment: 'Production' | 'Sandbox' = parsedState.environment || 'Production';
    setQuickbooksEnvironment(environment);
    setFormData(prev => ({ ...prev, ...restoredForm, realmId: realmId || prev.realmId }));

    // Exchange auth code for credentials
    let credentialPatch: Record<string, any> = { oauthCode: code };
    if (!isDemoMode) {
      try {
        const result = await fetchQuickbooksCredentials({
          variables: { input: { realmId: realmId || '', code, sandbox: environment === 'Sandbox' } },
        });
        const creds = result.data?.getQuickbooksCredentials;
        if (!creds) {
          setOauthError('Cannot make connection, Please try again.');
          setOauthConnecting(false);
          return;
        }
        // tokenExpiry is Unix seconds — convert to ISO string
        const expiryIso = new Date(creds.tokenExpiry * 1000).toISOString().split('.')[0] + 'Z';
        credentialPatch = {
          ...credentialPatch,
          clientId: creds.clientId,
          clientSecret: creds.clientSecret,
          accessToken: creds.accessToken,
          refreshToken: creds.refreshToken,
          tokenExpiryDate: expiryIso,
        };
      } catch (err) {
        console.error('Failed to fetch QuickBooks credentials', err);
        setOauthError('Cannot make connection, Please try again.');
        setOauthConnecting(false);
        return;
      }
    }

    const patch = {
      step2Completed: true,
      realmId: realmId || '',
      companyName: restoredForm.companyName || '',
      sourceName: restoredForm.sourceName || 'QuickBooks',
      startDate: restoredForm.startDate || getDefaultDate(),
      quickbooksEnvironment: environment,
      sandbox: environment === 'Sandbox',
      ...credentialPatch,
    };
    setStep2Completed(true);
    save(patch);
    sessionStorage.setItem(QB_SESSION_KEY, '1');
    setOauthConnecting(false);
    setActiveStep('test');
  };

  // ── OAuth callback — same-tab redirect ──────────────────────────────────────

  useEffect(() => {
    if (oauthHandled.current) return;
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const stateParam = urlParams.get('state');
    const realmIdFromUrl = urlParams.get('realmId');
    if (!code) return;
    let parsedState: Record<string, any> = {};
    try { parsedState = stateParam ? JSON.parse(decodeURIComponent(stateParam)) : {}; } catch { }
    if (parsedState.source !== 'quickbooks') return;
    oauthHandled.current = true;

    // New-tab mode: post back to parent and close
    if (window.opener) {
      window.opener.postMessage(
        { type: QB_OAUTH_POSTMESSAGE_TYPE, code, realmId: realmIdFromUrl || '', state: parsedState },
        window.location.origin
      );
      window.close();
      return;
    }

    // Same-tab fallback
    setOauthConnecting(true);
    applyOAuthCallback(code, realmIdFromUrl || '', parsedState);
    cleanOAuthParams();
  }, []);

  // ── OAuth callback — cross-tab postMessage ───────────────────────────────────

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== QB_OAUTH_POSTMESSAGE_TYPE) return;
      if (oauthHandled.current) return;
      oauthHandled.current = true;
      setOauthConnecting(true);
      applyOAuthCallback(event.data.code, event.data.realmId, event.data.state || {});
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // ── Invalidate connection on page refresh ────────────────────────────────────

  useEffect(() => {
    if (isDemoMode) return;
    if (saved.step2Completed && saved.accessToken && !sessionStorage.getItem(QB_SESSION_KEY)) {
      // Page was refreshed — credentials are no longer valid for this session
      updateSourceConfiguration('quickbooks', {
        step2Completed: false, accessToken: '', refreshToken: '',
        clientId: '', clientSecret: '', tokenExpiryDate: '', oauthCode: '',
      });
      setStep2Completed(false);
      setFormData(prev => ({ ...prev, realmId: '' }));
      setActiveStep('basic');
    }
  }, []);

  // ── Demo OAuth simulation ────────────────────────────────────────────────────

  const handleDemoConnect = async () => {
    setOauthConnecting(true);
    setOauthError(null);
    await new Promise(r => setTimeout(r, 1500));
    const demoRealmId = '1234567890';
    setFormData(prev => ({ ...prev, realmId: demoRealmId, companyName: 'Acme Corp (Demo)' }));
    const patch = {
      step2Completed: true, realmId: demoRealmId, companyName: 'Acme Corp (Demo)',
      sourceName: formData.sourceName, startDate: formData.startDate, datePeriod,
      quickbooksEnvironment, sandbox: quickbooksEnvironment === 'Sandbox',
    };
    setStep2Completed(true);
    save(patch);
    setOauthConnecting(false);
    setActiveStep('test');
  };

  // ── Step handlers ────────────────────────────────────────────────────────────

  const handleEnvironmentNext = () => {
    const patch = { step1Completed: true, environmentId: envId, quickbooksEnvironment, sandbox: quickbooksEnvironment === 'Sandbox' };
    setStep1Completed(true);
    save(patch);
    setActiveStep('basic');
  };

  const handleOAuthConnect = () => {
    setQuickBooksFormData(JSON.stringify(formData));
    const baseUrl = window.location.origin + window.location.pathname;
    const state = { step: 2, substep: 2, source: 'quickbooks', environment: quickbooksEnvironment };
    const clientId = quickbooksEnvironment === 'Production' ? QB_CLIENT_ID_PRODUCTION : QB_CLIENT_ID_SANDBOX;
    const params = new URLSearchParams({ response_type: 'code', client_id: clientId, scope: QB_SCOPE, redirect_uri: baseUrl, state: JSON.stringify(state) });
    window.open(`${QB_OAUTH_URL}?${params.toString()}`, '_blank', 'noopener');
  };

  const handleManualConnect = () => {
    const { clientId, clientSecret, accessToken, refreshToken, realmId, companyName } = manualCreds;
    if (!accessToken || !realmId) return;
    const patch = {
      step2Completed: true,
      realmId,
      companyName,
      sourceName: formData.sourceName,
      startDate: formData.startDate,
      datePeriod,
      quickbooksEnvironment,
      sandbox: quickbooksEnvironment === 'Sandbox',
      clientId,
      clientSecret,
      accessToken,
      refreshToken,
      tokenExpiryDate: '',
      oauthCode: '',
    };
    setFormData(prev => ({ ...prev, realmId, companyName }));
    setStep2Completed(true);
    save(patch);
    sessionStorage.setItem(QB_SESSION_KEY, '1');
    setActiveStep('test');
  };

  const handleReconnect = () => {
    setStep2Completed(false);
    setFormData(prev => ({ ...prev, realmId: '', companyName: '' }));
    save({ step2Completed: false, realmId: '', companyName: '' });
  };

  const runTest = async () => {
    setIsTesting(true);
    setTestCompleted(false);
    const reset = testingSteps.map(s => ({ ...s, status: 'pending' as TestStatus, errorMessage: undefined }));
    setTestingSteps(reset);
    let steps = reset.map(s => ({ ...s }));

    try {
      // Step 1 — Connection
      steps[0] = { ...steps[0], status: 'loading' };
      setTestingSteps([...steps]);
      await new Promise(r => setTimeout(r, 400));

      let response = null;
      if (isDemoMode) {
        await new Promise(r => setTimeout(r, 800));
        response = {
          connectionValid: true, groupIdFound: true,
          customerMetaDataKeys: ['account_id', 'company_id'],
          details: JSON.stringify({ accessibleEntities: ['customers', 'invoices', 'payments', 'items', 'credit_memos'], missingEntities: [] }),
          errorMessage: null,
        };
      } else {
        const result = await runTestBillingConnection({
          variables: {
            input: {
              integrationType: 'QUICKBOOKS',
              properties: {
                accessToken: saved.accessToken || '',
                refreshToken: saved.refreshToken || '',
                clientId: saved.clientId || '',
                clientSecret: saved.clientSecret || '',
                realmId: saved.realmId || '',
                tokenExpiryDate: saved.tokenExpiryDate || '',
                sandbox: saved.sandbox ?? false,
                environmentId: envId,
              },
            },
          },
        });
        response = result.data?.testBillingConnection ?? null;
      }

      if (!response?.connectionValid) {
        steps[0] = {
          ...steps[0],
          status: 'error',
          errorMessage: response?.errorMessage || 'Could not establish connection.',
          details: (response as any)?.details || undefined,
        };
        setTestingSteps([...steps]);
        setIsTesting(false);
        setTestCompleted(true);
        save({ testCompleted: true, step3Completed: false });
        return;
      }

      steps[0] = { ...steps[0], status: 'success' };
      setTestingSteps([...steps]);
      await new Promise(r => setTimeout(r, 500));

      // Step 2 — Data Retrieval
      steps[1] = { ...steps[1], status: 'loading' };
      setTestingSteps([...steps]);
      setTimeout(() => setIsTablesExpanded(true), 300);
      await new Promise(r => setTimeout(r, 700));

      let accessibleEntities: string[] = [];
      let missingEntities: string[] = [];
      try {
        const parsed = JSON.parse(response.details || '{}');
        accessibleEntities = parsed.accessibleEntities ?? [];
        missingEntities = parsed.missingEntities ?? [];
      } catch { /* details not JSON, skip */ }

      const hasMissingEntities = missingEntities.length > 0;
      steps[1] = { ...steps[1], status: hasMissingEntities ? 'error' : 'success', accessibleEntities, missingEntities };
      setTestingSteps([...steps]);
      await new Promise(r => setTimeout(r, 500));
      if (hasMissingEntities) setTimeout(() => setIsTablesExpanded(false), 1200);

      // Step 3 — Schema Verification
      steps[2] = { ...steps[2], status: 'loading' };
      setTestingSteps([...steps]);
      await new Promise(r => setTimeout(r, 600));

      const metaKeys = response.customerMetaDataKeys ?? [];
      const hasMetaKeys = metaKeys.length > 0;
      const schemaStatus: TestStatus = hasMetaKeys ? 'success' : 'error';
      steps[2] = { ...steps[2], status: schemaStatus };
      setTestingSteps([...steps]);

      setGroupIdFound(response.groupIdFound);
      setCustomerMetaDataKeys(metaKeys);
      setStep3Completed(true);
      setTestCompleted(true);
      const schemaOk = hasMetaKeys && response.groupIdFound;
      if (schemaOk) {
        setPauseFixCompleted(true);
        save({
          step3Completed: true,
          testCompleted: true,
          groupIdFound: response.groupIdFound,
          customerMetaDataKeys: metaKeys,
          pauseFixCompleted: true,
        });
        setActiveStep('recurring');
      } else {
        save({
          step3Completed: true,
          testCompleted: true,
          groupIdFound: response.groupIdFound,
          customerMetaDataKeys: metaKeys,
          pauseFixCompleted: false,
        });
        setActiveStep('pause-fix');
      }
    } catch (err) {
      console.error('QuickBooks test failed', err);
      steps = steps.map(s => s.status === 'loading' ? { ...s, status: 'error' as TestStatus } : s);
      setTestingSteps([...steps]);
      setTestCompleted(true);
      save({ testCompleted: true, step3Completed: false });
    } finally {
      setIsTesting(false);
    }
  };

  const handleStartSync = async () => {
    setIsSyncing(true);
    try {
      let ingestionResult: { syncStartedAt: string } | null = null;

      if (isDemoMode) {
        const result = await simulateSync({});
        ingestionResult = { syncStartedAt: result?.syncStartedAt ?? new Date().toISOString() };
      } else {
        const result = await createAirbyteIntegration({
          variables: {
            input: {
              environmentId: envId,
              integrationType: 'QUICKBOOKS' as any,
              syncStartTime: saved.startDate
                ? new Date(saved.startDate).toISOString().split('.')[0] + 'Z'
                : new Date().toISOString().split('.')[0] + 'Z',
              quickbooksConfig: {
                realmId: saved.realmId || '',
                clientId: saved.clientId || '',
                clientSecret: saved.clientSecret || '',
                accessToken: saved.accessToken || '',
                refreshToken: saved.refreshToken || '',
                tokenExpiryDate: saved.tokenExpiryDate || new Date().toISOString().split('.')[0] + 'Z',
                sandbox: saved.sandbox ?? false,
              },
            },
          },
        });
        if (result.data?.createAirbyteIntegration) {
          const res = result.data.createAirbyteIntegration;
          ingestionResult = { syncStartedAt: res.syncStartedAt ?? new Date().toISOString() };
        }
      }

      if (ingestionResult) {
        setSyncResult(ingestionResult);
        setStep4Completed(true);
        const completedAt = new Date().toISOString();
        save({
          step4Completed: true,
          ingestionResult,
          completedAt,
          status: 'configured',
          completed: true,
          notificationEmails: buildNotificationEmails(),
        });
        onTrackCompletion?.();
        onStepComplete(true);
      }
    } catch (err) {
      console.error('QuickBooks sync failed', err);
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => { if (step4Completed) onAutoSave(); }, [step4Completed]);

  useEffect(() => {
    if (isSyncing) {
      setSyncProgress(0);
      setSyncElapsed(0);
      const start = Date.now();
      syncTimerRef.current = setInterval(() => {
        const elapsed = (Date.now() - start) / 1000;
        setSyncElapsed(elapsed);
        if (elapsed < 5) {
          setSyncProgress((elapsed / 5) * 35);
        } else if (elapsed < 15) {
          setSyncProgress(35 + ((elapsed - 5) / 10) * 45);
        } else {
          setSyncProgress(prev => Math.min(93, prev + 0.05));
        }
      }, 100);
    } else {
      if (syncTimerRef.current) clearInterval(syncTimerRef.current);
      if (step4Completed) setSyncProgress(100);
    }
    return () => { if (syncTimerRef.current) clearInterval(syncTimerRef.current); };
  }, [isSyncing]);

  // ── Step completion map ──────────────────────────────────────────────────────

  const isCompleted = (id: StepId) => {
    if (id === 'environment')  return step1Completed;
    if (id === 'basic')        return step2Completed;
    if (id === 'test')         return step3Completed;
    if (id === 'pause-fix')    return pauseFixCompleted;
    if (id === 'recurring')    return itemsStepCompleted;
    if (id === 'plans')        return plansStepCompleted;
    if (id === 'professional')    return professionalStepCompleted;
    if (id === 'cancellations')   return cancellationsStepCompleted;
    if (id === 'ingestion')       return step4Completed;
    return false;
  };

  const isLocked = (id: StepId) => {
    if (id === 'basic')        return !step1Completed;
    if (id === 'test')         return !step2Completed;
    if (id === 'pause-fix')    return !step3Completed;
    if (id === 'recurring')    return !pauseFixCompleted;
    if (id === 'plans')        return !itemsStepCompleted;
    if (id === 'professional')  return !plansStepCompleted;
    if (id === 'cancellations') return !professionalStepCompleted;
    if (id === 'ingestion')     return !cancellationsStepCompleted;
    return false;
  };

  // ── Step content renderers ───────────────────────────────────────────────────

  const renderEnvironmentContent = () => (
    <div className="pb-2">
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Your QuickBooks</label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setEnvDropdownOpen(o => !o)}
              className="w-full flex items-center gap-2 px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <SiQuickbooks className="w-4 h-4 text-[#2CA01C] shrink-0" />
              <span className="flex-1 text-left">{quickbooksEnvironment}</span>
              {quickbooksEnvironment === recommendedQBEnvironment && (
                <span className="text-xs text-green-600 font-medium bg-green-50 px-1.5 py-0.5 rounded">Auto-mapped</span>
              )}
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${envDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {envDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                {(['Production', 'Sandbox'] as const).map(opt => {
                  const isSelected = quickbooksEnvironment === opt;
                  const isRecommended = opt === recommendedQBEnvironment;
                  const tooltip = !isSelected
                    ? `Switch to ${opt} — recommended for ${opt === 'Production' ? 'production' : 'non-production'} environments`
                    : undefined;
                  return (
                    <div key={opt} className="relative group">
                      <button
                        type="button"
                        onClick={() => { setQuickbooksEnvironment(opt); setEnvDropdownOpen(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${isSelected ? 'font-semibold text-primaryColor bg-blue-50' : 'text-gray-700'}`}
                      >
                        <SiQuickbooks className="w-4 h-4 text-[#2CA01C] shrink-0" />
                        <span className="flex-1">{opt}</span>
                        {isRecommended && (
                          <span className="text-xs text-green-600 font-medium">Recommended</span>
                        )}
                      </button>
                      {tooltip && (
                        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2.5 py-1.5 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                          {tooltip}
                          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">ThriveStack Environment</label>
          <div className="flex items-center gap-2 px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50">
            <span className="text-sm text-gray-700">{envDisplayName}</span>
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <button onClick={handleEnvironmentNext} className="px-4 py-2 rounded-lg text-sm font-semibold bg-primaryColor text-white hover:bg-primaryDarkColor transition-colors">
          Next
        </button>
      </div>
    </div>
  );

  const renderBasicContent = () => (
    <div className="pb-2 space-y-4">
      {!step2Completed && (
        <>
          <div>
            <LabelRow text="Source name" />
            <input type="text" value={formData.sourceName} onChange={e => setField('sourceName', e.target.value)} className={inputBase} />
          </div>
          <div>
            <LabelRow text="Start Date" />
            <div className="flex flex-wrap gap-2">
              {(['1m', '3m', '6m', '12m'] as const).map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => {
                    const d = computeStartDateFromPeriod(p);
                    setDatePeriod(p);
                    setField('startDate', d);
                    save({ datePeriod: p, startDate: d });
                  }}
                  className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${datePeriod === p ? 'border-primaryColor bg-primaryExtremeLightColor text-primaryColor' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'}`}
                >
                  {DATE_PERIOD_LABELS[p]}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setDatePeriod('custom')}
                className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${datePeriod === 'custom' ? 'border-primaryColor bg-primaryExtremeLightColor text-primaryColor' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'}`}
              >
                Custom
              </button>
            </div>
            {datePeriod === 'custom' && (
              <div className="relative mt-2">
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={e => { setField('startDate', e.target.value); save({ datePeriod: 'custom', startDate: e.target.value }); }}
                  className={`${inputBase} pr-10`}
                />
                <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            )}
          </div>
        </>
      )}

      {step2Completed && formData.realmId ? (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-green-800">Connected to QuickBooks</p>
                {formData.companyName && <p className="text-xs text-green-700 mt-0.5">{formData.companyName}</p>}
                <p className="text-xs text-green-600 mt-0.5 font-mono">Realm ID: {formData.realmId}</p>
              </div>
            </div>
            <button type="button" onClick={handleReconnect} className="flex items-center gap-1 text-xs text-green-700 hover:text-green-900 font-medium shrink-0 mt-0.5">
              <RefreshCw className="w-3.5 h-3.5" />Reconnect
            </button>
          </div>
        </div>
      ) : manualMode ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4 space-y-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-gray-800">Enter Credentials Manually</p>
            <button
              type="button"
              onClick={() => setManualMode(false)}
              className="text-xs text-primaryColor hover:underline font-medium"
            >
              ← Use OAuth instead
            </button>
          </div>
          {[
            { key: 'clientId',     label: 'Client ID',      placeholder: 'Your QuickBooks app Client ID' },
            { key: 'clientSecret', label: 'Client Secret',  placeholder: 'Your QuickBooks app Client Secret' },
            { key: 'accessToken',  label: 'Access Token',   placeholder: 'Bearer access token' },
            { key: 'refreshToken', label: 'Refresh Token',  placeholder: 'Refresh token' },
            { key: 'realmId',      label: 'Realm ID',       placeholder: 'QuickBooks company Realm ID' },
            { key: 'companyName',  label: 'Company Name',   placeholder: 'Your QuickBooks company name (optional)' },
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
              <input
                type={key === 'clientSecret' || key === 'accessToken' || key === 'refreshToken' ? 'password' : 'text'}
                value={manualCreds[key as keyof typeof manualCreds]}
                onChange={e => setManualCreds(prev => ({ ...prev, [key]: e.target.value }))}
                placeholder={placeholder}
                className={inputBase}
              />
            </div>
          ))}
          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={handleManualConnect}
              disabled={!manualCreds.accessToken || !manualCreds.realmId}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${manualCreds.accessToken && manualCreds.realmId ? 'bg-primaryColor text-white hover:bg-primaryDarkColor' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
            >
              Connect
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-5 flex flex-col items-center gap-3 text-center">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white border border-gray-200 shadow-sm">
            <SiQuickbooks className="w-6 h-6 text-[#2CA01C]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">Authorize with QuickBooks</p>
            <p className="text-xs text-gray-500 mt-1">
              You'll be redirected to Intuit to grant ThriveStack read access to your{' '}
              <span className="font-medium">{quickbooksEnvironment}</span> QuickBooks data.
            </p>
          </div>
          <button
            type="button"
            onClick={isDemoMode ? handleDemoConnect : handleOAuthConnect}
            disabled={oauthConnecting}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${oauthConnecting ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#2CA01C] text-white hover:bg-[#238016]'}`}
          >
            {oauthConnecting
              ? <><Loader2 className="w-4 h-4 animate-spin" />Connecting…</>
              : <><SiQuickbooks className="w-4 h-4" />Connect with QuickBooks</>
            }
          </button>
          {oauthError && <p className="text-xs text-red-600">{oauthError}</p>}
          {!isDemoMode && (
            <button
              type="button"
              onClick={() => setManualMode(true)}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              Enter Credentials manually
            </button>
          )}
        </div>
      )}

      {step2Completed && formData.realmId && (
        <div className="flex justify-end pt-1">
          <button onClick={() => setActiveStep('test')} className="px-4 py-2 rounded-lg text-sm font-semibold bg-primaryColor text-white hover:bg-primaryDarkColor transition-colors">
            Continue to Test
          </button>
        </div>
      )}
    </div>
  );

  const renderTestContent = () => {
    const hasFailed = testCompleted && testingSteps.some(s => s.status === 'error');
    const allPassed = testCompleted && testingSteps.every(s => s.status === 'success');
    const showGroupIdWarning = allPassed && !groupIdFound;

    return (
      <div className="pb-2 space-y-3">
        <div className="space-y-2">
          {testingSteps.map(step => (
            <div key={step.id}>
              <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${
                step.status === 'success' ? 'border-green-200 bg-green-50'
                : step.status === 'error'   ? 'border-red-200 bg-red-50'
                : step.status === 'loading' ? 'border-blue-200 bg-blue-50'
                : 'border-gray-200 bg-gray-50'
              }`}>
                {step.status === 'loading' ? <Loader2 className="w-4 h-4 text-blue-500 animate-spin shrink-0" />
                  : step.status === 'success' ? <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                  : step.status === 'error' ? <X className="w-4 h-4 text-red-500 shrink-0" />
                  : <div className="w-4 h-4 rounded-full border-2 border-gray-300 shrink-0" />}
                <span className={`text-sm font-medium ${
                  step.status === 'success' ? 'text-green-700'
                  : step.status === 'error'   ? 'text-red-700'
                  : step.status === 'loading' ? 'text-blue-700'
                  : 'text-gray-500'
                }`}>
                  {step.status === 'loading' ? step.loadingText
                    : step.status === 'success' ? step.successText
                    : step.status === 'error' ? step.errorText
                    : step.title}
                </span>
              </div>
              {/* Connection failure banner */}
              {step.id === 'connection' && step.status === 'error' && step.errorMessage && (
                <div className="mt-2 mx-1 px-4 py-3 rounded-lg border border-red-200 bg-red-50 space-y-1">
                  <p className="text-sm font-semibold text-red-800">{step.errorMessage}</p>
                  {step.details && (
                    <p className="text-xs text-red-700 leading-relaxed">{step.details}</p>
                  )}
                  <p className="text-xs text-red-600 mt-1">
                    Please verify your <span className="font-medium">Client ID</span>,{' '}
                    <span className="font-medium">Client Secret</span>,{' '}
                    <span className="font-medium">Access Token</span>, and{' '}
                    <span className="font-medium">Refresh Token</span> in the previous step and retry.
                  </p>
                </div>
              )}

              {/* Schema error — navigate to decision step */}
              {step.id === 'schema' && step.status === 'error' && (
                <div className="mt-3 px-3 py-3 rounded-lg border border-red-100 bg-red-50 flex items-start justify-between gap-3">
                  <p className="text-xs text-red-700">
                    We were not able to fetch the custom fields from your QuickBooks customers. Proceed to map your account fields.
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveStep('pause-fix')}
                    className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primaryColor text-white hover:bg-primaryDarkColor transition-colors"
                  >
                    Fix Now
                  </button>
                </div>
              )}

              {/* Entity list for data retrieval step */}
              {step.id === 'data' && step.status !== 'pending' && (step.accessibleEntities?.length ?? 0) + (step.missingEntities?.length ?? 0) > 0 && (
                <div className="mt-2 ml-1">
                  <button
                    type="button"
                    onClick={() => setIsTablesExpanded(v => !v)}
                    className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-sm font-medium mb-2"
                  >
                    <ChevronDown className={`w-4 h-4 transition-transform ${isTablesExpanded ? '' : '-rotate-90'}`} />
                    List of Entities Syncing
                  </button>
                  {isTablesExpanded && (
                    <div className="border-l-2 border-gray-200 pl-4 ml-2 space-y-3">
                      {(step.accessibleEntities?.length ?? 0) > 0 && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">
                            Accessible: <span className="font-medium text-gray-700">{step.accessibleEntities!.length}</span>
                          </p>
                          <ul className="space-y-1">
                            {step.accessibleEntities!.map(e => (
                              <li key={e} className="flex items-center gap-2 text-sm">
                                <Check className="w-4 h-4 text-green-600 shrink-0" />
                                <span className="text-gray-700">{e}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {(step.missingEntities?.length ?? 0) > 0 && (
                        <div>
                          <p className="text-xs text-red-500 mb-1">
                            Missing: <span className="font-medium">{step.missingEntities!.length}</span>
                          </p>
                          <ul className="space-y-1">
                            {step.missingEntities!.map(e => (
                              <li key={e} className="flex items-center gap-2 text-sm">
                                <X className="w-4 h-4 text-red-500 shrink-0" />
                                <span className="text-red-700 font-medium">{e}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {showGroupIdWarning && (
          <div className="space-y-3">
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-yellow-200 bg-yellow-50">
              <Info className="w-4 h-4 text-yellow-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Group ID field not found</p>
                <p className="text-xs text-yellow-700 mt-0.5">
                  The <span className="font-semibold">group_id</span> custom field was not found on your QuickBooks customers. Add it and populate it with the account/group identifier so account-level attribution works correctly.
                </p>
              </div>
            </div>
            <FixInstructionsContent source="quickbooks" accountIdentifierKey="group_id" />
            <button
              type="button"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm transition-colors"
              onClick={() => setShowInviteModal(true)}
            >
              Invite and Proceed
            </button>
          </div>
        )}

        {allPassed && customerMetaDataKeys.length > 0 && (
          <div className="px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50">
            <p className="text-xs font-medium text-gray-600 mb-1.5">Customer metadata keys found:</p>
            <div className="flex flex-wrap gap-1.5">
              {customerMetaDataKeys.map(k => (
                <span key={k} className="inline-block px-2 py-0.5 bg-white border border-gray-300 rounded text-xs text-gray-700 font-mono">{k}</span>
              ))}
            </div>
          </div>
        )}

        {!isTesting && (
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={runTest}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primaryColor text-white hover:bg-primaryDarkColor transition-colors"
            >
              {testCompleted ? 'Re-test Connection' : 'Test Connection'}
            </button>
            {hasFailed && (
              <p className="text-xs text-red-600">Fix the issue above and retry.</p>
            )}
          </div>
        )}

        {isTesting && (
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            Testing connection…
          </div>
        )}
      </div>
    );
  };

  const DAY_OPTIONS = [15, 30, 45, 60, 90];

  const renderCancellationsContent = () => (
    <div className="pb-2 space-y-4">
      <p className="text-sm font-semibold text-gray-800">Cancellations</p>

      {/* Option 1 — Auto cancel */}
      <div
        className={`rounded-xl border-2 transition-all ${cancellationPolicy === 'auto' ? 'border-primaryColor bg-primaryExtremeLightColor' : 'border-gray-200 bg-white'}`}
      >
        <button
          type="button"
          onClick={() => setCancellationPolicy('auto')}
          className="w-full flex items-center gap-3 px-4 py-3 text-left"
        >
          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${cancellationPolicy === 'auto' ? 'border-primaryColor' : 'border-gray-400'}`}>
            {cancellationPolicy === 'auto' && <div className="w-2 h-2 rounded-full bg-primaryColor" />}
          </div>
          <span className="text-sm font-medium text-gray-800 flex-1">Automatically mark cancel subscriptions</span>
        </button>

        {cancellationPolicy === 'auto' && (
          <div className="px-4 pb-4 space-y-3 border-t border-primaryColor/20 mt-1 pt-3">
            {/* When invoice unpaid */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-gray-700">When an invoice is unpaid</span>
                <Info className="w-3.5 h-3.5 text-gray-400" />
              </div>
              <div className="relative">
                <select
                  value={unpaidInvoiceDays}
                  onChange={e => setUnpaidInvoiceDays(Number(e.target.value))}
                  className="appearance-none pl-3 pr-8 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  {DAY_OPTIONS.map(d => (
                    <option key={d} value={d}>Cancel after {d} Days</option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              </div>
            </div>

            {/* When no renewal */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-gray-700">When there is no renewal invoice</span>
                <Info className="w-3.5 h-3.5 text-gray-400" />
              </div>
              <div className="relative">
                <select
                  value={noRenewalDays}
                  onChange={e => setNoRenewalDays(Number(e.target.value))}
                  className="appearance-none pl-3 pr-8 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  {DAY_OPTIONS.map(d => (
                    <option key={d} value={d}>Cancel after {d} Days</option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Option 2 — Manual */}
      <button
        type="button"
        onClick={() => setCancellationPolicy('manual')}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all ${cancellationPolicy === 'manual' ? 'border-primaryColor bg-primaryExtremeLightColor' : 'border-gray-200 bg-white hover:border-gray-300'}`}
      >
        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${cancellationPolicy === 'manual' ? 'border-primaryColor' : 'border-gray-400'}`}>
          {cancellationPolicy === 'manual' && <div className="w-2 h-2 rounded-full bg-primaryColor" />}
        </div>
        <span className="text-sm font-medium text-gray-800">Leave subscriptions as is until I explicitly mark them for cancellation</span>
      </button>

      <div className="flex justify-end pt-1">
        <button
          type="button"
          onClick={() => {
            setCancellationsStepCompleted(true);
            save({
              cancellationsStepCompleted: true,
              cancellationPolicy,
              unpaidInvoiceDays: cancellationPolicy === 'auto' ? unpaidInvoiceDays : null,
              noRenewalDays: cancellationPolicy === 'auto' ? noRenewalDays : null,
            });
            setActiveStep('ingestion');
          }}
          className="px-4 py-2 rounded-lg text-sm font-semibold bg-primaryColor text-white hover:bg-primaryDarkColor transition-colors"
        >
          Continue to Ingestion
        </button>
      </div>
    </div>
  );

  const renderIngestionContent = () => (
    <div className="pb-2 space-y-4">
      {step4Completed && syncResult ? (
        <>
          <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-green-200 bg-green-50">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-green-800">Sync started successfully</p>
              {syncResult.syncStartedAt && (
                <p className="text-xs text-green-600 mt-0.5">
                  Started at {new Date(syncResult.syncStartedAt).toLocaleString('en-US', {
                    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
                    hour: '2-digit', minute: '2-digit', second: '2-digit',
                  })}
                </p>
              )}
              <p className="text-xs text-green-700 mt-1">
                We're syncing your QuickBooks data. This may take 5–7 minutes. You'll receive an email notification once complete.
              </p>
            </div>
          </div>
          <div className="text-sm text-gray-700">
            You can continue to <strong>Core Revenue Calculation</strong> to set up currency and other fee-related configurations while the sync completes in the background.
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-600 mb-4">Your QuickBooks connection is configured and tested. Click below to start syncing your data to ThriveStack.</p>

          {/* Email notification section */}
          <div className="mb-4">
            <p className="text-sm text-gray-700 mb-3">
              Notification will be sent to: <span className="font-semibold">{getUser()?.email}</span>
            </p>
            {!emailManager.state.showInput ? (
              <button
                type="button"
                onClick={emailManager.toggleInput}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                Add Additional Emails
              </button>
            ) : (
              <div className="space-y-2">
                <label className="block text-sm text-gray-700">
                  Add additional email addresses where you'd like to receive notifications.
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={emailManager.state.inputValue}
                    onChange={(e) => emailManager.handleInputChange(e.target.value)}
                    onKeyDown={emailManager.handleKeyDown}
                    placeholder="Enter email and press Enter or comma"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${emailManager.state.error ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {emailManager.state.error && (
                    <p className="text-red-600 text-xs mt-1">{emailManager.state.error}</p>
                  )}
                </div>
                {emailManager.state.emails.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {emailManager.state.emails.map((email, index) => (
                      <div key={index} className="flex items-center gap-1 px-3 py-1 bg-gray-200 rounded-full text-sm">
                        <span>{email}</span>
                        <button
                          type="button"
                          onClick={() => emailManager.removeEmail(email)}
                          className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {isSyncing || createIntegrationLoading ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">
                  {syncElapsed < 5 ? 'Creating Source…' : syncElapsed < 15 ? 'Creating Connection…' : 'Starting Ingestion…'}
                </span>
                <span className="text-xs text-gray-400">{Math.round(syncProgress)}%</span>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${syncProgress}%` }}
                />
              </div>
            </div>
          ) : (
            <button
              onClick={handleStartSync}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primaryColor text-white hover:bg-primaryDarkColor transition-colors"
            >
              Start Ingestion
            </button>
          )}
        </>
      )}
    </div>
  );

  const renderItemTable = (
    items: { id: string; name: string; type: string; price: string }[],
    selected: Set<string>,
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    search: string,
    onSearchChange: (v: string) => void,
  ) => {
    const q = search.toLowerCase();
    const visible = items.filter(i => i.name.toLowerCase().includes(q) || i.type.toLowerCase().includes(q));
    const ids = visible.map(i => i.id);
    const allSelected = ids.length > 0 && ids.every(id => selected.has(id));
    const someSelected = ids.some(id => selected.has(id));

    const toggle = (id: string) => setter(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
    const toggleAll = () => setter(prev => { const next = new Set(prev); if (allSelected) ids.forEach(id => next.delete(id)); else ids.forEach(id => next.add(id)); return next; });

    return (
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search items…"
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          />
          {search && (
            <button type="button" onClick={() => onSearchChange('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-2.5 w-10">
                  <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }} onChange={toggleAll} className="w-4 h-4 accent-primaryColor rounded cursor-pointer" />
                </th>
                <th className="text-left px-4 py-2.5 font-medium text-gray-600">Name</th>
                <th className="text-left px-4 py-2.5 font-medium text-gray-600">Type</th>
                <th className="text-left px-4 py-2.5 font-medium text-gray-600">Price</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-400">No items match your search.</td></tr>
              ) : visible.map(item => {
                const checked = selected.has(item.id);
                return (
                  <tr key={item.id} onClick={() => toggle(item.id)} className={`border-b border-gray-100 last:border-0 cursor-pointer transition-colors ${checked ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                    <td className="px-4 py-3 text-center"><input type="checkbox" readOnly checked={checked} className="w-4 h-4 accent-primaryColor rounded cursor-pointer" /></td>
                    <td className="px-4 py-3 font-medium text-gray-900">{item.name}</td>
                    <td className="px-4 py-3 text-gray-500">{item.type}</td>
                    <td className="px-4 py-3 text-gray-900">{item.price}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const getItemsData = () => {
    const realItems = qbItemsData?.getQuickbooksItems?.items ?? [];
    const isLoadingReal = !isDemoMode && qbItemsLoading;
    const hasError = !isDemoMode && !!qbItemsError;
    const hasRealItems = !isDemoMode && realItems.length > 0;
    const showTable = isDemoMode || hasRealItems;
    const allItems: { id: string; name: string; type: string; price: string }[] = isDemoMode
      ? DEMO_ITEMS.map(i => ({ id: i.name, name: i.name, type: i.type, price: i.price }))
      : realItems.map(i => ({ id: i.id, name: i.name, type: i.type, price: `$${(i.unitPrice ?? 0).toFixed(2)}` }));
    return { isLoadingReal, hasError, hasRealItems, showTable, allItems };
  };

  const renderLoadingOrError = (isLoadingReal: boolean, hasError: boolean) => (
    isLoadingReal ? (
      <div className="border border-dashed border-gray-200 rounded-lg px-6 py-8 flex flex-col items-center gap-2 text-center bg-gray-50">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        <p className="text-sm font-medium text-gray-600">Fetching items from QuickBooks…</p>
      </div>
    ) : hasError ? (
      <div className="border border-dashed border-red-200 rounded-lg px-6 py-6 flex flex-col items-center gap-2 text-center bg-red-50">
        <p className="text-sm font-medium text-red-600">Failed to load items</p>
        <p className="text-xs text-red-400">{qbItemsError?.message}</p>
      </div>
    ) : (
      <div className="border border-dashed border-gray-200 rounded-lg px-6 py-6 flex flex-col items-center gap-2 text-center bg-gray-50">
        <p className="text-sm font-medium text-gray-600">No items found in your QuickBooks account.</p>
        <p className="text-xs text-gray-400">You can continue and update this selection later.</p>
      </div>
    )
  );

  const renderRecurringContent = () => {
    const { isLoadingReal, hasError, hasRealItems, showTable, allItems } = getItemsData();
    const canContinue = isDemoMode ? selectedItems.size > 0 : (hasRealItems ? selectedItems.size > 0 : true);
    return (
      <div className="pb-2 space-y-4">
        {showTable
          ? renderItemTable(allItems, selectedItems, setSelectedItems, itemSearch, setItemSearch)
          : renderLoadingOrError(isLoadingReal, hasError)}
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-gray-500">
            {selectedItems.size > 0
              ? `${selectedItems.size} item${selectedItems.size !== 1 ? 's' : ''} selected`
              : (isDemoMode || hasRealItems) ? 'Select at least one item to continue' : 'You can update your selection later.'}
          </span>
          <button
            onClick={() => {
              save({ itemsStepCompleted: true, mrrItems: Array.from(selectedItems) });
              setItemsStepCompleted(true);
              setActiveStep('plans');
            }}
            disabled={!canContinue}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${canContinue ? 'bg-primaryColor text-white hover:bg-primaryDarkColor' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  const renderPlansContent = () => {
    const { allItems } = getItemsData();
    const recurringItemsList = allItems.filter(i => selectedItems.has(i.id));
    const allConfigured = recurringItemsList.length > 0 && recurringItemsList.every(i => planTerms[i.id]?.term && planTerms[i.id]?.frequency);

    const updatePlan = (id: string, patch: Partial<PlanConfig>) => {
      setPlanTerms(prev => {
        const existing = prev[id] || { term: 'month' as PlanTerm, frequency: 1 };
        const updated = { ...existing, ...patch };
        if (patch.term && patch.term !== existing.term) {
          updated.frequency = TERM_FREQUENCIES[patch.term][0];
        }
        return { ...prev, [id]: updated };
      });
    };

    const selectBase = 'border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent px-3 py-2 pr-8 appearance-none cursor-pointer';

    return (
      <div className="pb-2 space-y-4">
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-2.5 font-medium text-gray-600">Product</th>
                <th className="text-left px-4 py-2.5 font-medium text-gray-600">Type</th>
                <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-36">Term</th>
                <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-44">Frequency</th>
              </tr>
            </thead>
            <tbody>
              {recurringItemsList.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-400">
                    No recurring items selected. Go back and select items first.
                  </td>
                </tr>
              ) : recurringItemsList.map(item => {
                const config = planTerms[item.id];
                const term = config?.term ?? ('' as any);
                const freqOptions = term ? TERM_FREQUENCIES[term as PlanTerm] : [];
                return (
                  <tr key={item.id} className="border-b border-gray-100 last:border-0">
                    <td className="px-4 py-3 font-medium text-gray-900">{item.name}</td>
                    <td className="px-4 py-3 text-gray-500">{item.type}</td>
                    <td className="px-4 py-3">
                      <div className="relative">
                        <select
                          value={term}
                          onChange={e => updatePlan(item.id, { term: e.target.value as PlanTerm })}
                          className={selectBase}
                        >
                          <option value="" disabled>Select term</option>
                          <option value="day">Day</option>
                          <option value="week">Week</option>
                          <option value="month">Month</option>
                          <option value="year">Year</option>
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="relative">
                        <select
                          value={config?.frequency ?? ''}
                          onChange={e => updatePlan(item.id, { frequency: Number(e.target.value) })}
                          disabled={!term}
                          className={`${selectBase} ${!term ? 'opacity-40 cursor-not-allowed' : ''}`}
                        >
                          <option value="" disabled>Select frequency</option>
                          {freqOptions.map(f => (
                            <option key={f} value={f}>{FREQUENCY_LABEL(term as PlanTerm, f)}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-gray-500">
            {allConfigured ? 'All products configured' : 'Set term and frequency for every product to continue'}
          </span>
          <button
            onClick={() => {
              save({ plansStepCompleted: true, mrrItemsTerm: { ...planTerms }, planTerms: null });
              setPlansStepCompleted(true);
              setActiveStep('professional');
            }}
            disabled={!allConfigured}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${allConfigured ? 'bg-primaryColor text-white hover:bg-primaryDarkColor' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  const renderProfessionalContent = () => {
    const { isLoadingReal, hasError, showTable, allItems } = getItemsData();
    const availableItems = allItems.filter(i => !selectedItems.has(i.id));
    return (
      <div className="pb-2 space-y-4">
        <p className="text-sm text-gray-600">
          Select the products and services that contribute to your revenue outside of recurring revenue — e.g. one-time fees, implementation, consulting, or training.
        </p>
        {showTable
          ? renderItemTable(availableItems, otherRevenueItems, setOtherRevenueItems, otherItemSearch, setOtherItemSearch)
          : renderLoadingOrError(isLoadingReal, hasError)}
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-gray-500">
            {otherRevenueItems.size > 0
              ? `${otherRevenueItems.size} item${otherRevenueItems.size !== 1 ? 's' : ''} selected`
              : 'Optional — you can update this later.'}
          </span>
          <button
            onClick={() => {
              save({ professionalStepCompleted: true, oneTimeItems: Array.from(otherRevenueItems), otherRevenueItems: null });
              setProfessionalStepCompleted(true);
              setActiveStep('cancellations');
            }}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors bg-primaryColor text-white hover:bg-primaryDarkColor"
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  const renderPauseFixContent = () => (
    <div className="pb-2 space-y-4">
      <p className="text-sm text-gray-700">
        We were not able to fetch the custom fields from your QuickBooks customers.{' '}
        <button
          type="button"
          onClick={() => setShowFixDrawer(true)}
          className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
        >
          Learn more
        </button>
      </p>

      {fieldMappingConfirmed ? (
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-green-200 bg-green-50">
          <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
          <p className="text-sm text-green-800">
            Mapped <span className="font-mono font-medium">{selectedProductField}</span> → <span className="font-mono font-medium">{selectedQbField === 'customerId' ? 'Id' : 'customer name'}</span>. ThriveStack will fetch account correlation from product telemetry.
          </p>
          <button
            type="button"
            onClick={() => setFieldMappingConfirmed(false)}
            className="ml-auto text-xs text-gray-400 hover:text-gray-600 underline shrink-0"
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <p className="text-sm font-medium text-gray-800">
            How do you map your accounts in product to your QuickBooks customers?
          </p>

          <div className="border border-gray-200 rounded-xl overflow-hidden">
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_24px_1fr] px-4 py-2 bg-gray-50 border-b border-gray-200">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Product</span>
              <span />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">QuickBooks</span>
            </div>

            {/* One row per mapping pair */}
            <div className="divide-y divide-gray-100">
              {([
                { productField: 'accountId' as const, qbField: 'customerId' as const, qbLabel: 'Id' },
                { productField: 'useremail' as const, qbField: 'customerName' as const, qbLabel: 'customer name' },
              ]).map(({ productField, qbField, qbLabel }) => {
                const isSelected = selectedProductField === productField && selectedQbField === qbField;
                return (
                  <button
                    key={productField}
                    type="button"
                    onClick={() => { setSelectedProductField(productField); setSelectedQbField(qbField); }}
                    className={`w-full grid grid-cols-[1fr_24px_1fr] items-center px-4 py-3 text-left transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                  >
                    <span className={`flex items-center gap-2 font-mono text-xs ${isSelected ? 'text-blue-700 font-semibold' : 'text-gray-700'}`}>
                      <div className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`} />
                      {productField}
                    </span>
                    <span className={`text-center text-sm font-bold ${isSelected ? 'text-blue-500' : 'text-gray-300'}`}>→</span>
                    <span className={`font-mono text-xs ${isSelected ? 'text-blue-700 font-semibold' : 'text-gray-700'}`}>{qbLabel}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              disabled={!selectedProductField || !selectedQbField}
              onClick={() => {
                setFieldMappingConfirmed(true);
                setPauseFixCompleted(true);
                save({ pauseFixCompleted: true, fieldMapping: { productField: selectedProductField, qbField: selectedQbField, confirmed: true } });
                setActiveStep('recurring');
              }}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-primaryColor text-white hover:bg-primaryDarkColor transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Confirm Mapping
            </button>
            <button
              type="button"
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Invite and Proceed
            </button>
            <button
              type="button"
              onClick={() => {
                setPauseFixCompleted(true);
                save({ pauseFixCompleted: true });
                setActiveStep('recurring');
              }}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors"
            >
              Proceed Anyway
            </button>
          </div>
        </>
      )}
    </div>
  );

  const renderContent = (id: StepId) => {
    if (id === 'environment')  return renderEnvironmentContent();
    if (id === 'basic')        return renderBasicContent();
    if (id === 'test')         return renderTestContent();
    if (id === 'pause-fix')    return renderPauseFixContent();
    if (id === 'recurring')    return renderRecurringContent();
    if (id === 'plans')        return renderPlansContent();
    if (id === 'professional')  return renderProfessionalContent();
    if (id === 'cancellations') return renderCancellationsContent();
    if (id === 'ingestion')     return renderIngestionContent();
    return null;
  };

  // ── Shell ────────────────────────────────────────────────────────────────────

  return (
    <div className="relative">
      {/* vertical connector line centered on w-8 circles */}
      <div className="absolute left-3.5 top-8 bottom-8 w-px bg-gray-200 z-0" />

      <div className="space-y-0">
        {STEPS.map((step) => {
          const completed = isCompleted(step.id);
          const locked = isLocked(step.id);
          const active = activeStep === step.id;

          return (
            <div key={step.id} className="relative z-10">
              {/* Step header row */}
              <div
                className={`flex items-start gap-4 py-3 ${locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={() => { if (!locked) setActiveStep(step.id); }}
              >
                {/* Step circle */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 border-2 text-sm font-bold z-10 mt-0.5 ${
                  completed ? 'bg-green-500 border-green-500 text-white'
                  : active   ? 'bg-primaryColor border-primaryColor text-white'
                  :            'bg-white border-gray-300 text-gray-400'
                }`}>
                  {completed ? <Check className="w-4 h-4" /> : step.num}
                </div>

                {/* Title + description */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`font-semibold text-sm ${active ? 'text-primaryColor' : locked ? 'text-gray-400' : 'text-gray-900'}`}>
                        {step.title}
                      </span>
                      {completed && (
                        <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">Completed</span>
                      )}
                    </div>
                    {!locked && (
                      <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${active ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                  <p className={`text-xs mt-0.5 ${locked ? 'text-gray-300' : 'text-gray-500'}`}>{step.description}</p>
                </div>
              </div>

              {/* Step content — indented to align with title */}
              {active && (
                <div className="ml-12 pb-4">
                  {renderContent(step.id)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Setup Guide drawer */}
      {showInviteModal && (
        <InviteTeamModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          team={TeamId.REVOPS_TEAMS}
          onInvite={() => {
            setPauseFixCompleted(true);
            save({ pauseFixCompleted: true });
            setActiveStep('recurring');
          }}
          source="quickbooks"
        />
      )}

      {showFixDrawer && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowFixDrawer(false)} />
          <div className="relative bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <span className="text-sm font-semibold text-gray-700">Fix Schema Issue</span>
              <button
                type="button"
                onClick={() => setShowFixDrawer(false)}
                className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-sm text-gray-700">
                The <span className="font-semibold font-mono">group_id</span> custom field is missing from your QuickBooks customers.
                Follow the instructions below to create it and populate it on every customer record.
              </p>
              <FixInstructionsContent source="quickbooks" accountIdentifierKey="group_id" />
            </div>
          </div>
        </div>
      )}

      {isSetupGuideOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setIsSetupGuideOpen(false)} />
          <div className="relative bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <span className="text-sm font-semibold text-gray-700">Setup Guide</span>
              <button
                type="button"
                onClick={() => setIsSetupGuideOpen(false)}
                className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              <QuickBooksSetupGuide onClose={() => setIsSetupGuideOpen(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
